import type { TranslationSchema } from "../types";

export const es = {
  common: {
    back: "Volver",
    next: "Siguiente",
    save: "Guardar",
    saved: "Guardado ✓",
    delete: "Eliminar",
    loading: "Cargando...",
    error: "Error",
    share: "Compartir",
    linkCopied: "¡Enlace copiado!",
    perPerson: "por persona",
    perNight: "/noche",
    days: "días",
    day: "día",
    travelers: "viajeros",
    traveler: "viajero",
    planTrip: "Planificar un viaje",
    letsGo: "¡Vamos!",
    continue: "Continuar",
    book: "Reservar",
    reviews: "reseñas",
    live: "En vivo",
    or: "o por email",
    confirm: "Confirmar",
    cancel: "Cancelar",
    close: "Cerrar",
    search: "Buscar",
    yes: "Sí",
    no: "No",
    appLoading: "Cargando…",
    sessionExpired: "Tu sesión ha caducado. Se cerró tu sesión.",
    signedOut: "Has cerrado sesión",
    seasonLow: "Temporada baja",
    seasonHigh: "Temporada alta",
    seasonTip: "Consejo de temporada",
    paywallSingleActive: "Búsqueda única activa — caduca el {date}",
    paywallNoAccess: "Acceso limitado — elige un plan",
    paywallFreeAvailable: "Plan Descubrimiento: 1 itinerario gratis este mes",
    paywallFreeUsed: "Ya usaste tu itinerario gratis de este mes — vuelve el próximo mes o desbloquea más búsquedas",
    paywallUpgrade: "Mejorar plan",
    paywallSingleExpired: "Búsqueda única caducada el {date}",
    paywallRenew: "Renovar",
    countryTopicsTitle: "Relatos por",
    countryTopicsHighlight: "país",
    countryTopicsSubtitle: "Toca un país para abrir su hilo de foro.",
    dismiss: "OK",
    previous: "Anterior",
  },
  notif: {
    title: "Notificaciones",
    markAllRead: "Marcar todo leído",
    empty: "Sin notificaciones",
    reply: "Nueva respuesta a tu comentario",
    comment: "Nuevo comentario en tu publicación",
    filterAll: "Todo",
    filterComments: "Comentarios",
    filterReplies: "Respuestas",
  },
  seasons: {
    tag: "Clima en vivo",
    title: "¿Cuándo viajar?",
    highlight: "temporadas en vivo",
    subtitle: "Datos climáticos reales vía API meteorológica, actualizados automáticamente.",
    recoLow: "Buen momento para ir",
    recoHigh: "Mucha gente ahora",
    recoNeutral: "Temporada intermedia",
  },
  calendar: {
    viewList: "Lista",
    viewCalendar: "Calendario",
    saveDraft: "Borrador",
    updateDraft: "Actualizar",
    draftSaved: "Borrador guardado",
    draftError: "Error al guardar",
    loginToSave: "Inicia sesión para guardar",
    addActivity: "Actividad",
    addPrompt: "¿Nombre de la actividad?",
    empty: "Sin actividades.",
    removed: "Actividad eliminada",
    dndHint: "Arrastra para reordenar o suelta sobre otro día.",
    movedToDay: "Actividad movida",
    regenerate: "Regenerar",
    regenerated: "Itinerario regenerado",
    daysKept: "días conservados",
    regenConfirmTitle: "¿Regenerar itinerario?",
    regenConfirmDesc: "Tus cambios en {n} día(s) se conservarán. Los demás días se recrearán según tus preferencias.",
    keepTastes: "Mantener mis gustos",
    lockPrefs: "Bloquear mis gustos (evita variaciones al regenerar)",
    tastesLocked: "Gustos bloqueados",
    unlockPrefs: "Preferencias bloqueadas: desmarca para permitir variación",
    untitledActivity: "Actividad",
    dragHandle: "Arrastrar",
    removeActivity: "Eliminar",
  },
  allInOne: {
    tag: "Todo en uno",
    title: "Tu vida de viajero,",
    highlight: "en un solo lugar",
    subtitle: "Mapa interactivo, hilos por país, notificaciones en tiempo real — todo lo que necesitas.",
    f1Title: "Mapa interactivo",
    f1Desc: "Marca países visitados, haz zoom y añade un relato en pocos clics.",
    f2Title: "Hilos auto por país",
    f2Desc: "Cada país añadido abre un hilo enriquecido por la comunidad.",
    f3Title: "Notificaciones en vivo",
    f3Desc: "Alertas in-app en tiempo real para comentarios y respuestas, filtrables.",
    f4Title: "Perfil viajero",
    f4Desc: "Bio, avatar, viajes compartidos y mapa público para otros viajeros.",
    f5Title: "Paywall flexible",
    f5Desc: "Búsqueda única 7 días, planes mensuales o anuales — cambia cuando quieras.",
    f6Title: "Sin conexión",
    f6Desc: "Tus itinerarios guardados siguen disponibles sin internet.",
  },
  nav: {
    myTrips: "Mis viajes",
    myLists: "Mis listas",
    login: "Iniciar sesión",
    logout: "Salir",
    settings: "Ajustes",
    search: "Buscar",
    newTrip: "Nuevo viaje",
    profile: "Perfil",
    dashboard: "Panel de control",
    signIn: "Iniciar sesión",
    signUp: "Registrarse",
    signOut: "Cerrar sesión",
    notSignedIn: "No conectado",
    discover: "Descubrir",
    account: "Mi cuenta",
    about: "Acerca de",
    discussion: "Discusión",
  },
  home: {
    title: "Prepara tu",
    titleHighlight: "Gran Partida",
    subtitle: "Tu viaje a medida en unos clics: ciudades, países o road trips, como quieras.",
    roadTripMode: "Modo Road Trip",
    roadTripDesc: "Varias ciudades en coche",
    departure: "Salida",
    destination: "Destino",
    viewGuide: "Ver guía",
    noGuideYet: "Aún no hay guía",
    countryUndetected: "País no detectado — revisa la ortografía",
    onlinePlaces: "Otros lugares (búsqueda en línea)",
    guidePreviewTitle: "Vista previa de la guía",
    viewFullGuide: "Ver guía completa",
    recommendedDuration: "Duración sugerida",
    budgetEstimate: "Presupuesto/día",
    customizeInterests: "Intereses",
    pickInterests: "Elige tus intereses",
    generatePreview: "Generar vista previa",
    refreshPreview: "Actualizar vista previa",
    dailyHighlights: "Día a día",
    planMyItinerary: "Planificar mi itinerario",
    durationIdeal: "Duración ideal para este país",
    durationTooShort: "Estancia algo corta",
    durationLong: "Estancia muy larga, considera varias regiones",
    idealLabel: "ideal",
    fillFieldsFirst: "Completa primero salida, destino y fechas.",
    interests: {
      label: "Intereses",
      culture: "Cultura",
      food: "Gastronomía",
      nature: "Naturaleza",
      beach: "Playas",
      adventure: "Aventura",
      nightlife: "Vida nocturna",
      shopping: "Compras",
      family: "Familia",
    },
    departurePlaceholder: "Madrid, Barcelona, París...",
    destinationPlaceholder: "Marruecos, Roma, Japón...",
    dates: "Fechas del viaje",
    selectDates: "Seleccionar fechas",
    travelers: "Viajeros",
    accommodationIncluded: "Alojamiento incluido",
    accommodationDesc: "Sugerencias de alojamiento en el itinerario",
    accommodationBudget: "Presupuesto alojamiento",
    addStop: "Añadir una parada",
    vehicleType: "Tipo de vehículo",
    fuelEstimate: "Estimación basada en",
    fuelSuffix: "L/100km a 1,75€/L",
    departureTime: "Hora de salida",
    departureTimeHint: "Se usa para calcular horas de conducción, pausas y llegada.",
    fuel: "combustible",
    arrivalLabel: "Llegada",
    stopLabel: "Parada",
    popularTitle: "Destinos",
    popularHighlight: "populares",
    popularSubtitle: "Déjate inspirar para tu próximo viaje",
    trendingTitle: "Destinos",
    trendingHighlight: "tendencia",
    trendingSubtitle: "Los destinos más buscados del momento",
    recommendedTitle: "Te",
    recommendedHighlight: "recomendamos",
    recommendedSubtitle: "Joyas escondidas para viajes inolvidables",
    plane: "Avión",
    train: "Tren",
    boat: "Barco",
    car: "Coche",
    randomDestination: "Destino aleatorio",
    destinationAlt: "destino de viaje",
    trendingBadge: "Tendencia",
    logoAlt: "Logo de Globr — planificador de viajes",
    decreaseTravelers: "Disminuir el número de viajeros",
    increaseTravelers: "Aumentar el número de viajeros",
    roadTripPaywallMessage: "Los road trips están disponibles a partir del plan Voyageur.",
    viewPlansAction: "Ver los planes",
    stopsQuotaPrefix: "Tu plan",
    stopsQuotaMiddle: "permite hasta",
    stopsQuotaUnit: "parada(s).",
    stopsQuotaSuffix: "Mejora a un plan superior para añadir más.",
    maxStopsReached: "Número máximo de paradas alcanzado",
    maxStopsReachedFor: "para el plan",
    upgradeButton: "Mejorar",
    seasonAnalyzing: "Buscando la mejor época para visitar",
    seasonLowHint: "menos turistas, mejores precios",
  },

  // Surprise destination quiz
  surprise: {
    button: "Destino sorpresa",
    title: "Encuentra mi destino sorpresa",
    subtitle: "Responde 6 preguntas y te sugerimos el viaje ideal.",
    resultSubtitle: "Aquí está tu destino perfecto ✨",
    searching: "Buscando tu destino ideal...",
    bestTime: "Mejor época",
    highlights: "No te pierdas",
    retry: "Empezar de nuevo",
    useDestination: "Usar este destino",
    errorTitle: "Ups",
    errorDesc: "No se pudo encontrar un destino, inténtalo de nuevo.",
    q: {
      vibe: "¿Qué ambiente buscas?",
      climate: "¿Qué clima prefieres?",
      budget: "¿Cuál es tu presupuesto?",
      duration: "¿Cuánto tiempo viajas?",
      activity: "¿Tu actividad favorita?",
      continent: "¿Algún continente preferido?",
    },
    vibe: {
      relax: "Relajación total",
      adventure: "Aventura",
      culture: "Cultura",
      party: "Fiesta",
      romantic: "Romántico",
      family: "En familia",
    },
    climate: {
      tropical: "Tropical y cálido",
      mild: "Suave y soleado",
      cold: "Fresco o frío",
      any: "Cualquiera",
    },
    budget: {
      low: "Presupuesto bajo",
      mid: "Confort",
      high: "Premium",
    },
    duration: {
      short: "Fin de semana",
      medium: "1-2 semanas",
      long: "3+ semanas",
    },
    activity: {
      beach: "Playa",
      hiking: "Senderismo",
      food: "Gastronomía",
      history: "Historia",
      city: "Ciudad",
      wildlife: "Vida salvaje",
    },
    continent: {
      europe: "Europa",
      asia: "Asia",
      americas: "Américas",
      africa: "África",
      oceania: "Oceanía",
      any: "Sorpréndeme",
    },
  },
  budget: {
    budget: "Económico",
    budgetDesc: "El mejor precio",
    value: "Calidad / Precio",
    valueDesc: "Buen equilibrio",
    luxury: "Lujo",
    luxuryDesc: "Premium y confort",
    unique: "Atípico",
    uniqueDesc: "Experiencias únicas",
    camping: "Camping",
    campingDesc: "Bajo las estrellas y furgoneta",
    autoFix: "Ajustar automáticamente este día",
    autoFixed: "Ajustes aplicados: ritmo «tranquilo» y se eliminó la actividad más cara.",
    exceedTitle: "Presupuesto diario superado",
  },
  questionnaire: {
    selectAll: "Selecciona todo lo que te corresponda",
    refineOptional: "Afinar (opcional)",
    selectOne: "Elige una opción",
    createItinerary: "Crear itinerario",
    createRoadTrip: "Crear road trip",
    interests: "Intereses",
    style: "Estilo de viaje",
    pace: "Ritmo del viaje",
    foodPref: "Preferencias culinarias",
    mobility: "Movilidad y accesibilidad",
    accommodation: "Alojamiento preferido",
    vibe: "Ambiente buscado",
    avoid: "Lo que quieres evitar",
    dailyBudget: "Presupuesto diario (por persona)",
    languages: "Idiomas hablados",
    culture: "🏛️ Cultura y Museos",
    nature: "🌿 Naturaleza y Senderismo",
    food: "🍽️ Gastronomía local",
    nightlife: "🌙 Vida nocturna",
    shopping: "🛍️ Compras",
    beach: "🏖️ Playa y Relax",
    adventure: "🧗 Deportes y Aventura",
    photo: "📸 Fotografía",
    history: "📜 Historia y Patrimonio",
    art: "🎨 Arte y Street art",
    wellness: "🧘 Bienestar y Spa",
    wildlife: "🦁 Fauna y Safari",
    offbeat: "🗺️ Fuera de lo común",
    backpacker: "🎒 Mochilero / Económico",
    comfort: "🏨 Confort",
    luxuryStyle: "💎 Lujo y Premium",
    local: "🏡 Casa local / Auténtico",
    chill: "Tranquilo — Disfrutar sin prisas",
    balanced: "Equilibrado — Mix visitas y descanso",
    intense: "Intenso — Ver lo máximo posible",
    streetFood: "🥙 Street food y mercados",
    localCuisine: "🍲 Cocina local tradicional",
    fineDining: "🍷 Restaurantes gastronómicos",
    vegetarian: "🥗 Vegetariano / Vegano",
    cafe: "☕ Cafeterías y brunchs",
    noConstraint: "🚶 Sin restricciones",
    limitedWalking: "🦶 Caminar limitado",
    wheelchair: "♿ Accesibilidad PMR",
    stroller: "👶 Cochecito de bebé",
    pet: "🐕 Con mascota",
    hotel: "🏨 Hotel",
    airbnb: "🏠 Airbnb / Alquiler",
    hostel: "🛏️ Albergue",
    campingAccom: "⛺ Camping / Furgoneta",
    boutique: "✨ Hotel boutique / Riad",
    romantic: "💕 Romántico",
    family: "👨‍👩‍👧 Familia",
    friends: "🎉 Amigos / Festivo",
    solo: "🧭 Solo / Introspección",
    hiddenGems: "💎 Joyas escondidas",
    instagram: "📱 Spots instagrameables",
    crowds: "👥 Multitudes y colas",
    touristTraps: "🪤 Trampas para turistas",
    longWalks: "🦿 Caminatas largas",
    earlyMornings: "😴 Madrugar",
    nothing: "✅ Nada en particular",
    budgetFree: "Gratis — Solo actividades gratuitas",
    budgetLow: "< 30€/día/pers.",
    budgetMid: "30-80€/día/pers.",
    budgetHigh: "80-150€/día/pers.",
    budgetPremium: "150€+/día/pers.",
    frenchOnly: "🇫🇷 Solo francés",
    english: "🇬🇧 Inglés",
    spanish: "🇪🇸 Español",
    localBasics: "🗣️ Nociones del idioma local",
    multilingual: "🌍 Multilingüe",
    activitiesPerDay: "Actividades por día",
    activities3to4: "3-4 — Ritmo tranquilo",
    activities5to6: "5-6 — Ritmo moderado",
    activities7to8: "7-8 — Ritmo activo",
    activities9plus: "9+ — Ritmo intenso",
    beachLakeDay: "Día de playa / baño",
    beachLakeDayDesc: "¿Te gustaría un día de playa o baño en un lago?",
    beachNone: "No gracias",
    beachHalfDay: "Media jornada playa / lago",
    beachFullDay: "Jornada completa playa / lago",
    beachMultiple: "Varios días de playa / lago",
    daySchedule: "Horarios del día",
    dayScheduleDesc: "¿A qué hora quieres empezar y terminar?",
    schedule8to20: "8h — 20h (Madrugador)",
    schedule9to21: "9h — 21h (Estándar)",
    schedule10to22: "10h — 22h (Flexible)",
    schedule10to23: "10h — 23h (Noctámbulo)",
    schedule11toMid: "11h — Medianoche (Fiestero)",
    restDays: "Días de descanso",
    restDays0: "Ninguno — programa completo",
    restDays1: "1 día de descanso",
    restDays2: "2 días de descanso",
    restDays3: "3 días de descanso",
  },
  loading: {
    roadTrip: "Planificando tu road trip...",
    itinerary: "Creando tu itinerario...",
    preparing: "Preparando tu aventura en",
    step1: "Analizando el destino...",
    step2: "Buscando los mejores spots...",
    step3: "Seleccionando restaurantes...",
    step4: "Finalizando el itinerario...",
    step5: "Eligiendo alojamientos...",
    factDuration: "Duración",
    factDates: "Fechas",
    factCurrency: "Moneda",
    factVisa: "Visado",
    visaRequired: "Requerido",
    visaFree: "No requerido",
    previewDays: "Vista previa del itinerario",
    galleryTitle: "Vista previa del destino",
    gallery: {
      monuments: "Monumentos",
      beaches: "Playas",
      neighborhoods: "Barrios",
      activities: "Actividades",
      food: "Gastronomía",
      nature: "Naturaleza",
    },
    galleryCaptions: {
      monuments: {
        default: "Joyas emblemáticas de {dest}",
        culture: "Patrimonio vivo por descubrir",
        history: "Tras las huellas del pasado de {dest}",
        art: "Arte y arquitectura al aire libre",
        photo: "Lugares icónicos para tus fotos",
        offbeat: "Monumentos secretos y poco conocidos",
      },
      beaches: {
        default: "Costas y aguas turquesa de {dest}",
        beach: "Playas perfectas para relajarse",
        wellness: "Calas tranquilas para desconectar",
        adventure: "Spots de surf, snorkel y buceo",
        photo: "Atardeceres impresionantes",
        offbeat: "Playas secretas lejos de las multitudes",
      },
      neighborhoods: {
        default: "Barrios para recorrer en {dest}",
        culture: "Calles llenas de historia y alma",
        offbeat: "Barrios locales fuera de las rutas turísticas",
        shopping: "Tiendas, mercados y creadores",
        art: "Arte urbano y galerías independientes",
        photo: "Fachadas y ambientes muy fotogénicos",
      },
      activities: {
        default: "Experiencias para vivir en {dest}",
        adventure: "Emociones fuertes y desafíos al aire libre",
        wellness: "Pausas de bienestar y spa",
        nightlife: "{dest} cobra vida de noche",
        family: "Actividades para disfrutar en familia",
        offbeat: "Experiencias únicas e insólitas",
      },
      food: {
        default: "Sabores imprescindibles de {dest}",
        food: "Mesas, mercados y especialidades locales",
        nightlife: "Bares y rincones que no duermen",
        offbeat: "Comedores secretos y cocinas de barrio",
        culture: "La cocina, espejo de la cultura local",
      },
      nature: {
        default: "Paisajes naturales alrededor de {dest}",
        nature: "Espacios salvajes y grandes panoramas",
        adventure: "Rutas, cumbres y aventura al aire libre",
        wildlife: "Fauna y flora para observar",
        wellness: "Escapadas verdes para respirar",
        photo: "Decorados grandiosos para la fotografía",
      },
    },
    editCaption: "Editar leyenda",
    saveCaption: "Guardar",
    resetCaption: "Restablecer",
    captionPlaceholder: "Tu leyenda personalizada…",
    validateTitle: "Revisa tu vista previa",
    validateSubtitle: "Ajusta las leyendas y luego inicia la generación",
    confirmAndGenerate: "Confirmar y generar",
    backToQuestions: "Volver a las preguntas",
    contributingTags: "Inspirado en",
    customCaption: "Personalizada",
    tipsTitle: "¿Sabías que?",
    tipsSourceGuide: "Extraído de la guía local de {destination}",
    tipsSourceVerified: "Fuente verificada",
    tipsSourceLoading: "Leyendo la guía del destino…",
    // Paso 1 — Análisis del destino
    tip1: "Revisa el clima y la temporada alta antes de reservar — lo cambia todo allí.",
    tip2: "Aprende 5 palabras en el idioma local — \"hola\" y \"gracias\" abren puertas.",
    // Paso 2 — Búsqueda de lugares
    tip3: "Madrugar y la hora dorada: la mejor luz para fotos y menos multitudes.",
    tip4: "Combina lugares icónicos con rincones poco conocidos para una experiencia auténtica.",
    // Paso 3 — Restaurantes
    tip5: "Prueba la comida callejera: suele ser la más auténtica y económica.",
    tip6: "Pregunta a los locales dónde comen — los mejores sitios rara vez están en las guías.",
    // Paso 4 — Alojamiento
    tip7: "Un barrio céntrico vale más que un hotel de lujo lejos: ahorras horas cada día.",
    tip8: "Lee reseñas recientes: un buen sitio puede cambiar en pocos meses.",
    // Paso 5 — Finalización
    tip9: "Guarda copias digitales de tus documentos importantes en la nube.",
    tip10: "Reserva tus actividades imprescindibles con antelación, pero deja espacio a la improvisación.",
  },
  results: {
    transportOptions: "Opciones de transporte",
    transportSubtitle: "Selecciona tu modo de transporte preferido.",
    continueBtn: "Continuar — Planificar el itinerario",
    roadRoute: "Ruta por carretera",
    vehicleType: "Tipo de vehículo",
    departureIcon: "Salida",
    arrivalIcon: "Llegada",
    tollIcon: "Peaje",
    fuelIcon: "Gasolinera",
  },
  itinerary: {
    roadTrip: "Road Trip",
    itineraryLabel: "Itinerario",
    summary: "Resumen",
    chatPlaceholder: "Modificar el itinerario...",
    updated: "¡Itinerario actualizado!",
    nightSpot: "Spot noche",
    park4night: "Park4Night",
    franciaPassion: "France Passion",
    nightsTitle: "Duración por etapa",
    nightsHint: "Define el número de noches por ciudad (el total debe coincidir con la duración del viaje).",
    nightsUnit: "noches",
    nightsMismatch: "Total de noches ({n}) ≠ duración del viaje ({d}). Ajusta para que coincida con el plan.",
    noSelectionYet: "Aún no hay selecciones",
    toggleView: "Cambiar vista",
  },
  summary: {
    title: "Resumen",
    destination: "Destino",
    duration: "Duración",
    travelersLabel: "Viajeros",
    transport: "Transporte",
    selectedTransport: "Transporte seleccionado",
    estimatedBudget: "Presupuesto estimado",
    transportBudget: "Transporte",
    activitiesBudget: "Actividades",
    foodBudget: "Restauración",
    totalEstimated: "Total estimado",
    mapTitle: "Mapa del itinerario",
    pdf: "PDF",
    loginToSave: "Inicia sesión para guardar",
    tripSaved: "¡Viaje guardado!",
    publicLink: "Enlace público",
    tagline: "TU ITINERARIO PERSONALIZADO",
    printToc: "Índice",
    printDayLabel: "Día",
    activitiesCountLabel: "actividades",
    printPageLabel: "Página",
    bonVoyage: "¡Buen viaje! 🌍✈️",
    printFooterDesc: "Itinerario generado por Globr. Encuentra y edita tu viaje en mongrandepart.lovable.app",
    allRightsReserved: "Todos los derechos reservados",
    recoBadge: "Recomendado",
    publicShareRestricted: "Compartir de forma pública está disponible desde el plan Viajero.",
  },
  auth: {
    welcomeBack: "¡Bienvenido de nuevo!",
    createAccount: "Crear cuenta",
    loginSubtitle: "Inicia sesión para encontrar tus viajes",
    signupSubtitle: "Regístrate para guardar tus itinerarios",
    displayName: "Nombre para mostrar",
    namePlaceholder: "Tu nombre",
    email: "Email",
    emailPlaceholder: "tu@ejemplo.com",
    password: "Contraseña",
    login: "Iniciar sesión",
    signup: "Registrarse",
    noAccount: "¿No tienes cuenta?",
    hasAccount: "¿Ya tienes cuenta?",
    loginSuccess: "¡Conexión exitosa!",
    checkEmail: "¡Revisa tu email para confirmar tu registro!",
    authError: "Error de autenticación",
    forgotPassword: "¿Olvidaste tu contraseña?",
    forgotDesc: "Ingresa tu email para recibir un enlace de restablecimiento",
    sendResetLink: "Enviar enlace",
    backToLogin: "Volver al inicio de sesión",
    resetEmailSent: "¡Email de restablecimiento enviado!",
    passwordMismatch: "Las contraseñas no coinciden",
    passwordTooShort: "La contraseña debe tener al menos 6 caracteres",
    passwordUpdated: "¡Contraseña actualizada!",
    redirecting: "Redirigiendo...",
    invalidResetLink: "Enlace inválido",
    invalidResetLinkDesc: "Este enlace de restablecimiento no es válido o ha expirado.",
    backToLoginBtn: "Volver al inicio de sesión",
    newPassword: "Nueva contraseña",
    newPasswordDesc: "Ingresa tu nueva contraseña",
    confirmPassword: "Confirmar contraseña",
    updatePassword: "Actualizar",
    changePassword: "Cambiar contraseña",
    currentPassword: "Contraseña actual",
    passwordChanged: "¡Contraseña cambiada con éxito!",
    changePasswordDesc: "Ingresa tu nueva contraseña",
    promoCodeLabel: "Código promocional (opcional)",
    promoCodePlaceholder: "Ingresa tu código promo",
    promoCodeHint: "¿Tienes un código promo? Ingrésalo para obtener búsquedas gratuitas.",
    promoApplied: "¡Código promocional aplicado con éxito!",
    promoInvalid: "Código promocional inválido o expirado.",
    referralCodeLabel: "Código de referido (opcional)",
    referralCodePlaceholder: "Ingresa el código de un amigo",
    referralCodeHint: "¿Un amigo te refirió? Ingresa su código para búsquedas bonus.",
    referralApplied: "¡Referido aplicado! Búsquedas bonus añadidas.",
    referralInvalid: "Código de referido inválido.",
    acceptTermsLabel: "Acepto las CGU/CGV y reconozco que toda suscripción (mensual o anual) implica un compromiso mínimo de 12 meses, renovable tácitamente.",
    acceptTermsRequired: "Debes aceptar las CGU/CGV para crear una cuenta.",
    cguLink: "CGU/CGV",
  },
  myTrips: {
    title: "Mis viajes",
    noTrips: "Sin viajes guardados",
    noTripsDesc: "¡Planifica tu primer viaje y guárdalo!",
    loadError: "Error al cargar viajes",
    deleteSuccess: "Viaje eliminado",
    deleteError: "Error al eliminar",
    filterAll: "Todos",
    filterDrafts: "Borradores",
    filterFinal: "Viajes",
    draftBadge: "Borrador",
    actionsLabel: "Acciones",
    favoriteLabel: "Favorito",
    tripPromptTitle: "¿Qué tal fue ese viaje?",
    tripPromptDesc: "Comparte tu experiencia en {city} con la comunidad: consejos, recomendaciones, anécdotas...",
    tripPromptCta: "Compartir mi experiencia",
    tripPromptDismiss: "No, gracias",
    tripPromptDefaultTitlePrefix: "Mi experiencia en",
  },
  myLists: {
    title: "Mis listas",
    noLists: "Sin listas",
    noListsDesc: "Guarda búsquedas en listas desde tus resultados.",
    noSearches: "Sin búsquedas guardadas",
    deleteSuccess: "Lista eliminada",
    searchDeleteSuccess: "Búsqueda eliminada",
    deleteError: "Error al eliminar",
  },
  preferences: {
    defaultsApplied: "Tus preferencias se han aplicado",
    readyTripApplied: "Viaje listo cargado — ajústalo antes de generar",
    reuseTitle: "¿Reutilizar tus preferencias?",
    reuseDesc: "Tienes {n} respuestas guardadas en tu cuenta. Reutilízalas ahora o empieza de cero.",
    startFresh: "Nuevas preferencias",
    reuse: "Reutilizar",
    saveAsDefaults: "Mis preferencias",
    saveAsDefaultsTitle: "Guardar estos filtros y gustos como preferencias predeterminadas en mi cuenta",
    saved: "Preferencias guardadas en tu cuenta",
  },
  discussion: {
    title: "Discusión de viajeros",
    subtitle: "Conecta con la comunidad: experiencias, consejos y recomendaciones.",
    myProfile: "Mi perfil",
    searchPlaceholder: "Buscar un tema, una ciudad...",
    newPost: "Nuevo tema",
    locationPlaceholder: "Lugar (ciudad, país...) — opcional",
    pickCountry: "País relacionado — opcional",
    publish: "Publicar",
    hideMap: "Ocultar mapa",
    showMap: "Filtrar en el mapa mundial",
    empty: "No hay temas para este filtro. ¡Sé el primero en iniciar la conversación!",
    save: "Guardar",
    saved: "Guardado",
    unsaved: "Eliminado de tus favoritos",
    reuseItinerary: "Reutilizar itinerario",
    report: "Denunciar",
    reply: "Responder",
    loadMoreReplies: "Cargar {n} respuestas más",
    replyingTo: "Respondiendo a un comentario",
    replyPlaceholder: "Escribe tu respuesta...",
    reportTitle: "Denunciar este contenido",
    reportDesc: "Ayúdanos a mantener la comunidad segura. Describe brevemente el problema (spam, contenido inapropiado, información falsa…).",
    reportPlaceholder: "Motivo de la denuncia (opcional)",
    confirmReport: "Confirmar denuncia",
    alreadyReported: "Ya has denunciado este contenido.",
    reportSent: "Denuncia enviada. ¡Gracias!",
    titlePlaceholder: "Título",
    contentPlaceholder: "Comparte tu experiencia, tu pregunta...",
    like: "Me gusta",
    send: "Enviar",
    titleContentRequired: "Título (mín. 3) y contenido (mín. 10) obligatorios",
    postPublished: "¡Publicación creada!",
    photosLabel: "Fotos",
    addPhotoButton: "Añadir",
    deletePhotoLabel: "Eliminar foto",
    photosWord: "fotos",
    eachWord: "cada una",
    photoAlt: "Foto",
    uploadingPhoto: "Enviando foto",
    rateLimited: "¡Estás publicando demasiado rápido! Inténtalo de nuevo en unos minutos.",
    paywallDesc: "La discusión entre viajeros está reservada para los suscriptores {plans}. Comparte experiencias, consejos y recomendaciones con la comunidad.",
    paywallPlans: "Explorer y Familia",
    paywallCta: "Descubrir los planes",
    cat: {
      all: "Todos",
      travel: "Viajes",
      app: "La app",
      city: "Ciudades",
      activity: "Actividades",
    },
  },
  legal: {
    title: "Información legal",
    mentions: "Aviso legal",
    cgu: "Condiciones de uso",
    privacy: "Política de privacidad",
  },
  notFound: {
    title: "404",
    message: "¡Ups! Página no encontrada",
    goHome: "Volver al inicio",
  },
  testimonials: {
    title: "Lo que opinan nuestros",
    titleHighlight: "viajeros",
    subtitle: "+5.000 itinerarios creados — Únete a la comunidad",
  },
  footer: {
    mentions: "Aviso legal",
    cgu: "Condiciones de uso",
    privacy: "Política de privacidad",
    guide: "Nuestra Guía",
    rights: "Todos los derechos reservados.",
    tagline: "La comunidad de viajeros",
  },
  continents: {
    europe: "Europa",
    africa: "África",
    asia: "Asia",
    americas: "Américas",
    oceania: "Oceanía",
    middleEast: "Oriente Medio",
  },
  countries: {
    maroc: "Marruecos",
    espagne: "España",
    japon: "Japón",
    italie: "Italia",
    portugal: "Portugal",
    thailande: "Tailandia",
    turquie: "Turquía",
    etatsunis: "Estados Unidos",
  },
  airport: {
    selectTitle: "Elegir un aeropuerto de llegada",
    selectSubtitle: "Aeropuertos disponibles",
    sortedByProximity: "Ordenados por proximidad desde",
    closest: "Más cercano",
    change: "Cambiar",
  },
  map: {
    clickToSelect: "Haz clic en el mapa para elegir un destino",
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    reset: "Restablecer",
    showAll: "Todos",
    showVisited: "Visitados",
    showUnvisited: "Por descubrir",
  },
  landing: {
    seoTitle: "Globr — Planifica tu viaje a medida",
    seoDescription: "Itinerarios personalizados, comparación multi-transporte, guía de visados y mapas interactivos para más de 30 000 ciudades y países.",
    heroTag: "Hecho por viajeros, para viajeros",
    heroTitle1: "Viaja tranquilo,",
    heroTitle2: "sin el precio de una agencia",
    heroSubtitle: "Se acabaron las horas comparando vuelos, hoteles y actividades en diez pestañas distintas. Nosotros nos encargamos de todo y te damos un itinerario a medida — hasta un 70 % más barato que una agencia de viajes tradicional.",
    heroPriceBadge: "💶 Hasta un 70 % más barato que una agencia",
    heroTrustNote: "Sin compromiso · Cancela cuando quieras · Primer itinerario gratis",
    startFree: "Crear mi itinerario",
    pricingSingleTitle: "Búsqueda única",
    pricingSinglePrice: "5€",
    pricingSingleUnit: "/ 1 búsqueda",
    pricingSingleDesc: "Disponible 7 días en tu cuenta",
    pricingSingleItems: "1 itinerario generado,Disponible 7 días,Sin compartir,Sin acceso al foro",
    pricingSingleCta: "Comprar una búsqueda",
    pricingPerTitle: "Explorer",
    pricingPerPrice: "12,99€",
    pricingPerUnit: "/ mes o 99€/año",
    pricingPerDesc: "El más popular",
    pricingPerPopular: "Recomendado",
    pricingPerItems: "Itinerarios ilimitados,Hoteles incluidos,PDF limpio (sin marca de agua),Personalización avanzada,Acceso al foro y mapa comunidad",
    pricingPerCta: "Elegir Explorer",
    pricingYearTitle: "Pro / Agencia",
    pricingYearPrice: "29,99€",
    pricingYearUnit: "/ mes o 249€/año",
    pricingYearDesc: "Conserjerías y blogueros de viaje",
    pricingYearItems: "Búsquedas ilimitadas,PDF marca blanca,API y exportación,Multicuenta,Soporte prioritario",
    pricingYearCta: "Elegir Pro",
    pricingBillingMonthly: "Mensual",
    pricingBillingYearly: "Anual",
    pricingBillingSave: "−36%",
    pricingSingleFor: "Para probar sin compromiso — pago único",
    pricingPerFor: "Para viajeros habituales — suscripción",
    pricingYearFor: "Para pros, agencias y creadores — suscripción",
    pricingPerPriceM: "12,99€",
    pricingPerUnitM: "/ mes",
    pricingPerPriceY: "99€",
    pricingPerUnitY: "/ año",
    pricingPerHintM: "Sin compromiso, cancela cuando quieras",
    pricingPerHintY: "Equivale a 8,25€/mes — 2 meses gratis",
    pricingYearPriceM: "29,99€",
    pricingYearUnitM: "/ mes",
    pricingYearPriceY: "249€",
    pricingYearUnitY: "/ año",
    pricingYearHintM: "Sin compromiso, cancela cuando quieras",
    pricingYearHintY: "Equivale a 20,75€/mes — 2 meses gratis",
    testimonialsTitle: "Lo que dicen",
    testimonialsTitleHighlight: "nuestros viajeros",
    testimonial1Name: "Sophie L.",
    testimonial1Text: "Planifiqué mi road trip por Italia en 2 minutos. ¡El itinerario fue perfecto!",
    testimonial1City: "París",
    testimonial2Name: "Marc D.",
    testimonial2Text: "El PDF exportado es hermoso, se lo mostré a todo mi grupo y seguimos el plan al pie de la letra.",
    testimonial2City: "Lyon",
    testimonial3Name: "Amina K.",
    testimonial3Text: "Elegir Japón como país y tener los aeropuertos ordenados es genial. Ahorro de tiempo enorme.",
    testimonial3City: "Bruselas",
    faqTitle: "Preguntas",
    faqTitleHighlight: "frecuentes",
    faq1Q: "¿Cómo funciona la generación de itinerarios?",
    faq1A: "Nuestro travel planner analiza tu destino, fechas, presupuesto y preferencias para crear un programa a medida con actividades, restaurantes y transporte local optimizados.",
    faq2Q: "¿Puedo elegir un país entero?",
    faq2A: "¡Sí! Cuando seleccionas un país, la app te sugiere automáticamente los aeropuertos de llegada ordenados por proximidad a tu primer punto de interés.",
    faq3Q: "¿Cómo funciona el modo Road Trip?",
    faq3A: "Activa el modo Road Trip para planificar un viaje en coche con hasta 8 paradas intermedias, cálculo automático de combustible y peajes, y gasolineras en la ruta.",
    faq4Q: "¿Puedo compartir mi itinerario?",
    faq4A: "Por supuesto. Genera un enlace público que cualquiera puede ver sin crear cuenta, o exporta un PDF profesional.",
    faq5Q: "¿Qué métodos de pago se aceptan?",
    faq5A: "Todos los pagos (búsqueda única y suscripciones) son procesados por Stripe, nuestro proveedor de pago seguro certificado PCI-DSS. Aceptamos tarjetas Visa, Mastercard y American Express. Ningún dato bancario se almacena en nuestros servidores.",
    faq6Q: "¿Puedo modificar un itinerario después de generarlo?",
    faq6A: "Puedes guardar tus itinerarios en tus listas y regenerar con diferentes parámetros en cualquier momento.",
    ctaTitle: "¿Listo para la",
    ctaTitleHighlight: "gran partida",
    ctaSubtitle: "Sin agencia, sin costes ocultos: tu itinerario a medida en minutos, hasta un 70 % más barato.",
    ctaBtn: "Planificar mi viaje",
    navHow: "Funcionamiento",
    navFeatures: "Funcionalidades",
    navPricing: "Precios",
    navFaq: "FAQ",
    plan: "Mi Espacio",
    login: "Iniciar sesión",
    mockRoute: "París → Tokio",
    mockDays: "7 días",
    mockActivities: "12 actividades",
    statsItineraries: "Itinerarios creados",
    mobileTitle: "Lleva Globr a todas partes",
    mobileDesc: "Instala la aplicación en tu teléfono para acceder sin conexión, como una app real.",
    mobileIos1: "Abre el sitio en Safari",
    mobileIos2: "Toca el icono de compartir",
    mobileIos3: "Selecciona 'Añadir a pantalla de inicio'",
    mobileAndroid1: "Abre el sitio en Chrome",
    mobileAndroid2: "Toca el menú ⋮",
    mobileAndroid3: "Selecciona 'Añadir a pantalla de inicio'",
    visaGuideTag: "Guía práctica",
    visaGuideTitle: "Visa, trabajo, estudios:",
    visaGuideHighlight: "todo antes de viajar",
    visaGuideDesc: "Para cada país: condiciones de visado, permisos de trabajo, visas de estudio, doble nacionalidad, coste de vida. Prepara tu salida con confianza.",
    visaGuideCta: "Ver la guía de visas",
    visaGuideLink: "Consulta la guía de visas para",
    howItWorks: "Cómo funciona",
    howTitle: "Tu viaje en",
    howTitleHighlight: "4 pasos sencillos",
    howSubtitle: "De la idea a la salida — nos ocupamos de todo.",
    step1Title: "Elige tu destino",
    step1Desc: "País, ciudad o inspiración: explora más de 30 000 ciudades y países con búsqueda inteligente.",
    step2Title: "Indica tus fechas y presupuesto",
    step2Desc: "Dinos tus fechas, número de viajeros y presupuesto para un itinerario a medida.",
    step3Title: "Recibe tu itinerario",
    step3Desc: "Recibes un plan día a día con actividades, restaurantes y transporte, listo para seguir.",
    step4Title: "Viaja con tranquilidad",
    step4Desc: "Exporta a PDF, comparte con tus seres queridos y accede a tu viaje sin conexión.",
    featuresTitle: "Todo lo que necesitas",
    featuresTitleHighlight: "para viajar bien",
    featuresSubtitle: "Un kit completo para planificar, comparar y disfrutar.",
    feature1Title: "Itinerarios personalizados",
    feature1Desc: "De 6 a 10 actividades al día según tus intereses, presupuesto y temporada.",
    feature2Title: "Comparación de transportes",
    feature2Desc: "Vuelos, trenes, autobuses y coches comparados en tiempo real con enlaces directos.",
    feature3Title: "Visado y seguridad",
    feature3Desc: "Requisitos de entrada, vacunas y nivel de seguridad actualizados para 200+ países.",
    feature4Title: "Modo road trip",
    feature4Desc: "Hasta 8 etapas con cálculo de combustible, peajes y áreas de descanso.",
    feature5Title: "Mapas interactivos",
    feature5Desc: "Visualiza tu itinerario en un mapa con actividades, hoteles y puntos de interés.",
    feature6Title: "Exportación PDF y compartir",
    feature6Desc: "PDF elegante de 1 día por página y enlace público seguro para compartir en un clic.",
    pricingTitle: "Precios",
    pricingTitleHighlight: "simples y claros",
    pricingSubtitle: "Prueba gratis, paga solo si te encanta.",
    trustTravelers: "+12 800 viajeros",
    trustDestinations: "+30 000 ciudades cubiertas",
    heroCities: "+30 000 ciudades cubiertas en todo el mundo",
    communityTag: "Comunidad",
    communityTitle: "Únete a una comunidad",
    communityTitleHighlight: "de viajeros apasionados",
    communitySubtitle: "Comparte tus itinerarios, descubre los de otros e inspírate con historias del mundo.",
    communityFeat1Title: "Mapa mundial",
    communityFeat1Desc: "Ve qué países ha visitado la comunidad y encuentra nueva inspiración.",
    communityFeat2Title: "Foro de intercambio",
    communityFeat2Desc: "Haz preguntas, comparte consejos y lee experiencias de otros miembros.",
    communityFeat3Title: "Perfiles de viajeros",
    communityFeat3Desc: "Sigue a otros trotamundos e inspírate con sus itinerarios públicos.",
    communityCta: "Explorar el mapa",
    communityProfileCta: "Ver perfiles",
    communityMapHint: "Haz clic en un país para explorar los viajes de la comunidad.",
    heroImageAlt: "Destino de viaje Globr — imagen",
  },
  admin: {
    title: "Administración",
    users: "Usuarios",
    search: "Buscar...",
    noName: "Sin nombre",
    registeredOn: "Registrado el",
    loading: "Cargando...",
    noUsers: "No se encontraron usuarios",
    deleteConfirm: "Eliminar",
    roleAdded: "Rol añadido",
    roleRemoved: "Rol eliminado",
    loadError: "Error de carga",
    deleteSuccess: "Usuario eliminado",
    deleteError: "Error al eliminar",
    back: "Volver",
  },
  shared: {
    title: "Itinerario compartido",
    notFound: "Itinerario no encontrado",
    notFoundDesc: "Este enlace de compartir no es válido o el itinerario ha sido eliminado.",
    backHome: "Volver al inicio",
    createdWith: "Creado con Globr",
    back: "Volver",
    share: "Compartir",
    linkCopied: "¡Enlace copiado!",
    destination: "Destino",
    duration: "Duración",
    travelers: "Viajeros",
    departure: "Salida",
    days: "{n} días",
    day: "Día",
    planTrip: "Planificar mi viaje",
    filtersTitle: "Filtros y preferencias de este viaje",
  },

  settings: {
    title: "Configuración",
    profile: "Perfil",
    displayName: "Nombre visible",
    connectedVia: "Conectado vía",
    changeEmail: "Cambiar email",
    changeEmailDesc: "Se enviará un correo de confirmación a ambas direcciones.",
    newEmail: "Nuevo email",
    updateEmail: "Actualizar email",
    emailConfirmSent: "Se ha enviado un correo de confirmación.",
    changePassword: "Cambiar contraseña",
    changePasswordDesc: "Mínimo 6 caracteres.",
    newPassword: "Nueva contraseña",
    confirmPassword: "Confirmar contraseña",
    updatePassword: "Actualizar contraseña",
    passwordMismatch: "Las contraseñas no coinciden.",
    passwordTooShort: "La contraseña debe tener al menos 6 caracteres.",
    passwordUpdated: "Contraseña actualizada con éxito.",
    nameUpdated: "Nombre actualizado.",
    profilePhoto: "Foto de perfil",
    profilePhotoDesc: "Haz clic para cambiar (máx. 2MB)",
    avatarUpdated: "Foto de perfil actualizada.",
    subscription: "Suscripción",
    subscriptionDesc: "Gestiona tu plan y pagos.",
    freePlan: "Sin suscripción activa",
    freePlanDesc: "1 itinerario gratis cada mes — o elige una búsqueda única (5 €) o una suscripción Voyageur, Explorer o Pro vía Stripe.",
    singleSearchPaid: "¡Búsqueda desbloqueada por 7 días!",
    offlineReassurance: "Todos tus viajes, incluso en el plan gratuito Descubrimiento, siguen disponibles sin conexión.",
    active: "Activo",
    upgradePlan: "Mejorar plan",
    perSearchPlan: "Por búsqueda",
    perSearchDesc: "Pago único para una búsqueda de itinerario completa.",
    buySearch: "Comprar una búsqueda",
    annualPlan: "Ilimitado anual",
    annualDesc: "Búsquedas ilimitadas durante 1 año.",
    subscribe: "Suscribirse",
    bestValue: "Mejor valor",
    renewsOn: "Se renueva el",
    manageSubscription: "Gestionar mi suscripción",
    refreshStatus: "Actualizar estado",
    checkoutSuccess: "¡Pago exitoso! Tu suscripción está activa.",
    checkoutCancelled: "Pago cancelado.",
    signOut: "Cerrar sesión",
    deleteAccount: "Eliminar mi cuenta",
    deleteAccountTitle: "¿Eliminar tu cuenta permanentemente?",
    deleteAccountWarning: "Esta acción es irreversible. Todos tus datos (perfil, viajes, listas, búsquedas, contribuciones, fotos de avatar) se eliminarán definitivamente, conforme al RGPD.",
    deleteAccountTypeToConfirm: "Escribe ELIMINAR para confirmar:",
    deleteAccountConfirm: "Eliminar permanentemente",
    deleteAccountConfirmRequired: "Escribe ELIMINAR para confirmar",
    accountDeleted: "Cuenta eliminada.",
    deleteAccountHint: "La eliminación es permanente y conforme al RGPD.",
    referral: "Referidos",
    referralDesc: "Comparte tu código y gana búsquedas gratis.",
    yourReferralCode: "Tu código de referido",
    copyCode: "Copiar",
    codeCopied: "¡Código copiado!",
    referralCount: "Referidos",
    referralBonus: "búsquedas bonus ganadas",
    shareReferral: "Compartir mi código",
    paymentPending: "El pago se está validando…",
    avatarMaxSize: "Tamaño máximo: 2 MB",
    avatarUploadError: "Error al subir la foto",
    checkoutError: "Error en el pago",
    deleteAccountError: "Error al eliminar la cuenta",
    deleteAccountConfirmWord: "ELIMINAR",
    manageFamilyProfiles: "Gestionar perfiles familiares",
    newEmailPlaceholder: "nuevo@email.com",
    singleSearchTitle: "Búsqueda única",
    noCommitment: "Sin compromiso",
    perSearchUnit: "/ 1 búsqueda",
    singleSearchFeature1: "1 itinerario generado",
    singleSearchFeature2: "Visible en tu cuenta durante 7 días",
    singleSearchFeature3: "Sin compartir, sin acceso al foro",
    buySingleSearchButton: "Comprar una búsqueda (5 €)",
    billingMonthly: "Mensual",
    billingYearly: "Anual",
    billingYearlyDiscount: "hasta -36%",
    recommendedBadge: "Recomendado",
    plans: {
      voyageur: {
        name: "Viajero",
        feature1: "10 búsquedas/mes",
        feature2: "Exportación en PDF",
        feature3: "Road trip de 5 etapas",
        feature4: "Compartición ilimitada",
        cta: "Elegir Viajero",
      },
      explorer: {
        name: "Explorer",
        feature1: "40 búsquedas/mes",
        feature2: "Road trip de 8 etapas",
        feature3: "Perfiles familiares ilimitados",
        feature4: "Moderación prioritaria",
        cta: "Elegir Explorer",
      },
      pro: {
        name: "Pro / Agencia",
        feature1: "Búsquedas ilimitadas",
        feature2: "PDF de marca blanca",
        feature3: "API / exportación",
        feature4: "Cuentas múltiples",
        cta: "Elegir Pro",
      },
    },
    referralShareTextPrefix: "Únete a Globr con mi código",
    referralShareTextSuffix: "¡y obtén búsquedas gratis!",
  },

  signupBanner: {
    title: "Crea tu cuenta para continuar",
    desc: "Regístrate para guardar tus itinerarios y acceder a las búsquedas vía Stripe (búsqueda única o suscripción).",
    cta: "Crear mi cuenta",
    login: "Ya tengo una cuenta",
  },

  guide: {
    tag: "Guía práctica",
    title: "Guía de",
    titleHighlight: "Visas y Trámites",
    subtitle: "Todo lo que necesitas saber sobre visas, permisos de trabajo, estudios y expatriación para cada destino.",
    searchPlaceholder: "Buscar un país...",
    allContinents: "Todos los continentes",
    visaRequired: "Visa requerida",
    visaFree: "Sin visa",
    tourism: "Turismo",
    tourismShort: "Turismo",
    work: "Trabajar",
    workShort: "Trabajo",
    study: "Estudiar",
    studyShort: "Estudios",
    live: "Vivir allí",
    liveShort: "Vivir",
    visaStatus: "Estado del visa",
    duration: "Estancia autorizada",
    cost: "Costo",
    requirements: "Requisitos",
    tips: "Consejos prácticos",
    applyEVisa: "Solicitar e-Visa",
    dualNationality: "Doble nacionalidad",
    allowed: "Permitida",
    notAllowed: "No permitida",
    healthSystem: "Sistema de salud",
    costOfLiving: "Costo de vida",
    disclaimer: "Esta información se proporciona como guía general y puede cambiar. Consulte siempre los sitios web oficiales del gobierno para los requisitos más actualizados.",
    translating: "Traduciendo…",
    currencyTitle: "Conversor de divisa",
    currencyError: "Tipo de cambio no disponible. Inténtalo de nuevo más tarde.",
    currencySwap: "Invertir conversión",
    currencyLive: "Tipo actualizado diariamente",
    currencyRefresh: "Actualizar tipo",
    currencyDisclaimer: "Tipo indicativo proporcionado a título informativo. Fuente:",
    currencyUpdatedOn: "actualizado el",
    eurCountryTitle: "Este país utiliza el euro (€)",
    eurCountryNote: "No se necesita conversor: la moneda local es la misma que la tuya si viajas desde la zona euro.",
    lastTranslated: "Última traducción",
    clearTranslationCache: "Forzar una nueva traducción",
    cacheBannerTitle: "Datos mostrados desde la caché local",
    cacheBannerDesc: "La traducción y el tipo de cambio pueden estar desactualizados. Actualiza para obtener los últimos valores.",
    cacheBannerRefresh: "Actualizar todo",
    cacheBannerRefreshing: "Actualizando…",
    cacheBannerRefreshingDesc: "Recuperando la última traducción y el tipo de cambio más reciente.",
    cacheTranslationLabel: "Traducción en caché desde",
    cacheFxLabel: "Tipo de cambio en caché desde",
    cacheStale: "expirado",
    enrichmentTitle: "Información práctica para preparar tu partida",
    enrichLanguages: "Idiomas hablados",
    enrichTimezone: "Zona horaria",
    enrichElectricity: "Electricidad",
    enrichBestTime: "Mejor época",
    enrichEmergency: "Números de emergencia",
    enrichSafety: "Seguridad",
    enrichPopularCities: "Ciudades populares",
    enrichSalary: "Salarios",
    enrichTaxation: "Fiscalidad",
    enrichConnectivity: "Conectividad",
    enrichTransport: "Transporte",
    enrichItineraries: "Itinerarios tipo",
    budgetBackpacker: "Presupuesto mochilero / día",
    budgetMid: "Presupuesto confort / día",
    budgetComfort: "Presupuesto premium / día",
    enrichCulturalTips: "Consejos culturales",
    enrichScams: "Estafas comunes",
    enrichHealth: "Salud y vacunas",
    enrichAccommodation: "Encontrar alojamiento",
    enrichApps: "Apps útiles",
    enrichOfficial: "Sitios oficiales",
    safetyLevel: { low: "Riesgo bajo", medium: "Precaución moderada", high: "Alta precaución" },
    enrichEmbassy: "Embajada / Consulado",
    embassyAddress: "Dirección",
    embassyPhone: "Teléfono",
    embassyWebsite: "Sitio oficial",
    safetyRecommendationsTitle: "Recomendaciones según el nivel",
    safetyRecos: {
      low: [
        "Vigilancia estándar en zonas turísticas (carteristas).",
        "Guarde una copia digital de sus documentos (pasaporte, visa).",
        "Contrate un seguro de viaje básico.",
      ],
      medium: [
        "Evite caminar solo de noche en barrios poco transitados.",
        "Prefiera apps oficiales de VTC en vez de taxis en la calle.",
        "Consulte regularmente las recomendaciones oficiales de viaje.",
        "Contrate un seguro que incluya evacuación médica.",
      ],
      high: [
        "Regístrese en el programa consular de su país.",
        "Limite los desplazamientos terrestres y prefiera vuelos entre regiones.",
        "Evite manifestaciones públicas y zonas fronterizas sensibles.",
        "Tenga los contactos de emergencia de la embajada a mano.",
        "Contrate un seguro integral con repatriación y cobertura de riesgo político.",
      ],
    },
    faqTitleEnrich: "Preguntas frecuentes sobre",
    jumpToHealth: "Ir a la sección de salud",
    jumpToAccommodation: "Ir a la sección de alojamiento",
    jumpToTransport: "Ir a la sección de transporte",
    jumpToSafety: "Ir a la sección de seguridad",
    jumpToCurrency: "Ir al conversor de divisas",
    relatedSections: "Secciones relacionadas",
    faq: {
      visaQ: "¿Necesito visa para {country}?",
      visaA: "{answer}",
      visaAYes: "Sí, se requiere visa. Duración: {duration}.",
      visaANo: "No, {country} es accesible sin visa para muchas nacionalidades. {visaFreeFor} Duración: {duration}.",
      durationQ: "¿Cuánto tiempo puedo quedarme?",
      durationA: "{duration}",
      bestTimeQ: "¿Cuál es la mejor época para viajar?",
      bestTimeA: "{bestTime}",
      currencyQ: "¿Qué moneda se usa en {country}?",
      currencyA: "La moneda local es el {name} ({code}, símbolo {symbol}).",
      currencyEurA: "{country} usa el euro (€), no se necesita conversión desde la zona euro.",
      safetyQ: "¿El país es seguro para los viajeros?",
      safetyA: "{note}",
      healthQ: "¿Se requieren vacunas específicas?",
      healthA: "{advice}",
      languageQ: "¿Qué idiomas se hablan?",
      languageA: "Idiomas principales: {langs}.",
      costQ: "¿Cuál es el costo de vida?",
      costA: "{cost}",
    },
    breadcrumbHome: "Inicio",
    currency: "Moneda",
    practical: "Información práctica",
    h1: "Guía completa de visas y trámites administrativos",
    h1Country: "Visa, trabajo, estudios y expatriación",
    sectionTourism: "Visa de turismo",
    sectionWork: "Trabajar allí",
    sectionStudy: "Estudiar allí",
    sectionLive: "Instalarse a largo plazo",
    seoTitle: "Guía de Visas y Trámites Administrativos | Globr",
    seoDescription: "Guía práctica de visas, permisos de trabajo, estudios y expatriación para más de 27 países. Condiciones, duración, costos y consejos para preparar tu partida.",
    seoTitleCountry: "Visa {country}: turismo, trabajo, estudios, expatriación | Globr",
    seoDescriptionCountry: "Todo sobre las visas para {country}: condiciones de estancia, permisos de trabajo, visas de estudiante, doble nacionalidad y costo de vida.",
    sectionsNav: "Secciones de la guía",
    clearSearch: "Borrar",
    continentFiltersNav: "Filtros por continente",
    tocNav: "Índice",
  },

  nav2: {
    guide: "Nuestra Guía",
  },

  plan: {
    recapTitle: "Resumen del viaje",
    recapSubtitle: "Revisa la información antes de generar tu itinerario completo.",
    budget: "Nivel de presupuesto",
    travelMode: "Modo de transporte",
    flightMode: "Avión / Tren",
    noInterests: "Sin intereses seleccionados",
    openGuideTitle: "Ver guía completa",
    openGuideDesc: "Visa, salud, seguridad, costo de vida y consejos prácticos.",
    disclaimer: "Esta vista previa es indicativa. El itinerario final se genera a partir de tus criterios.",
    editTrip: "Editar",
    launchSearch: "Lanzar búsqueda",
  },
  offline: {
    title: "Sin conexión",
    intro: "Viajes disponibles sin conexión:",
    youAreOffline: "Estás sin conexión — esto es lo disponible:",
    empty: "Aún no hay viajes sin conexión",
    emptyDesc: "Desde Mis viajes, elige «Guardar sin conexión».",
    keepAction: "Guardar sin conexión",
    removeAction: "Quitar de sin conexión",
    saved: "Disponible sin conexión",
    removed: "Quitado de sin conexión",
    viewItinerary: "Ver itinerario",
    syncNow: "Sincronizar",
    synced: "Viajes sincronizados",
    upToDate: "Ya está al día",
    syncError: "Error al sincronizar",
    lastSync: "Última sync",
    serverDeletedTitle: "Eliminados en el servidor",
    serverDeletedDesc: "Estos viajes ya no existen en el servidor. ¿Quitarlos del caché sin conexión?",
    keep: "Mantener",
    remove: "Quitar",
    confirmRemoveTitle: "¿Quitar de sin conexión?",
    confirmRemoveDesc: "Este viaje fue eliminado del servidor. Quitarlo del caché borrará la copia local de forma permanente.",
    conflictsTitle: "Conflictos de sincronización",
    conflictsDesc: "La versión del servidor sustituyó tus cambios sin conexión. Se conservó una copia local:",
  },

  plans: {
    free: { shortLabel: "Descubrimiento" },
    voyageur: { shortLabel: "Voyageur" },
    explorer: { shortLabel: "Explorer" },
    pro: { shortLabel: "Pro / Agencia" },
  },

  theme: {
    enableLight: "Activar el tema claro",
    enableDark: "Activar el tema oscuro",
  },

  mobile: {
    home: "Inicio",
    quickSearchPlaceholder: "¿A dónde quieres ir?",
    quickSearchLabel: "Búsqueda rápida",
    quickStart: "Inicio rápido",
  },

  routeMap: {
    departure: "Salida",
    arrival: "Llegada",
    tollZone: "Zona de peaje",
  },

  activityCard: {
    bookRide: "Reservar un trayecto:",
    transitMiniPlan: "Mini plan de trayecto",
    departure: "Salida",
    arrival: "Llegada",
    doorToDoor: "Puerta a puerta",
    margin: "Margen",
    understandMargin: "Entender el margen",
    show: "mostrar",
    hide: "ocultar",
    advanceHoursTitle: "Horas de antelación recomendadas según el medio",
    modeFlight: "Avión",
    modeTrain: "Tren",
    modeFerry: "Ferry",
    modeBus: "Autobús",
    advance: {
      flight: {
        time: "+2h a 3h",
        usage: "Llega 2h antes (Schengen) o 3h antes (internacional): facturación, entrega de equipaje, controles.",
      },
      train: {
        time: "+20 a 30 min",
        usage: "Preséntate en la estación 20–30 min antes: retirar el billete, acceder al andén, ubicar tu asiento.",
      },
      ferry: {
        time: "+1h a 2h",
        usage: "1h a pie, 2h con vehículo: embarque, controles, carga.",
      },
      bus: {
        time: "+15 a 20 min",
        usage: "Llega 15–20 min antes: ubicar el andén, guardar el equipaje, embarcar.",
      },
      car: {
        time: "+15 min",
        usage: "Margen para el tráfico, aparcar y repostar antes de salir.",
      },
      default: {
        time: "+30 min",
        usage: "Margen de seguridad estándar para imprevistos y conexiones.",
      },
    },
    marginExplainer: "Este margen se suma al tiempo puerta a puerta para calcular tu hora de salida y garantizar una transición tranquila entre ciudades.",
    compareOptionsTitle: "Opciones de trayecto — compara antes de confirmar",
    fast: "Rápido",
    cheap: "Económico",
    recommended: "Recomendado",
    chosen: "Elegido",
    toConfirm: "Por confirmar",
    advancePrefix: "Antelación",
    comparisonTitle: "Comparativa antes de confirmar",
    current: "Actual",
    plannedChoice: "Elección prevista",
    durationDeltaLabel: "Duración",
    budgetDeltaLabel: "Presupuesto del día",
    before: "Antes",
    after: "Después",
    same: "sin cambios",
    unchangedHalfDays: "Medios días sin cambios",
    marginVsSuffix: "vs {margin} actual",
    confirmChoice: "Confirmar esta opción",
  },

  saveToList: {
    trigger: "Lista",
    title: "Guardar en una lista",
    createPlaceholder: "Crear una nueva lista...",
    createError: "Error al crear la lista",
    saveError: "Error al guardar",
    searchSaved: "¡Búsqueda guardada!",
    emptyState: "Crea tu primera lista arriba",
  },

  shareLinks: {
    ttl7: "7 días",
    ttl30: "30 días",
    ttl90: "90 días",
    ttl365: "1 año",
    title: "Enlaces públicos para compartir",
    description: "Cada enlace caduca automáticamente y puede regenerarse o revocarse en cualquier momento.",
    duration: "Duración",
    create: "Crear",
    empty: "Ningún enlace activo. Crea uno arriba.",
    statusActive: "Activo",
    statusRevoked: "Revocado",
    statusExpired: "Expirado",
    createdOn: "Creado el",
    expiresOn: "Caduca el",
    revokedOn: "Revocado el",
    copy: "Copiar",
    regenerate: "Regenerar",
    revoke: "Revocar",
    revokeConfirm: "¿Revocar este enlace? Ya no será accesible.",
    linkCopied: "¡Enlace copiado!",
    linkCreatedCopied: "Enlace creado y copiado",
    tokenRegenerated: "Token regenerado y copiado: el anterior ya no funciona",
    linkRevoked: "Enlace revocado",
    genericError: "Error",
  },

  family: {
    title: "Perfiles familiares",
    subtitle: "Crea hasta 5 perfiles para generar itinerarios que combinen los gustos de toda la familia.",
    subscriptionRequired: "Se requiere una suscripción",
    voyageurLimitMessage: "El plan Viajero permite 1 perfil. Pasa a Explorer para perfiles ilimitados.",
    limitReached: "Límite alcanzado",
    nameRequired: "Se requiere un nombre",
    profileAdded: "Perfil añadido",
    profileDeleted: "Perfil eliminado",
    upsellTitle: "Función Familia",
    upsellDescPart1: "Pasa a",
    upsellVoyageurOffer: "Viajero (6,99 €/mes)",
    upsellDescPart2: "para 1 perfil, o",
    upsellExplorerOffer: "Explorer (12,99 €/mes)",
    upsellDescPart3: "para perfiles familiares ilimitados.",
    viewPlansButton: "Ver los planes",
    addProfileTitle: "Añadir un perfil",
    firstName: "Nombre",
    age: "Edad (opcional)",
    avatarLabel: "Avatar",
    interestsLabel: "Intereses",
    addButton: "Añadir",
    noProfiles: "Aún no hay perfiles.",
    yearsUnit: "años",
  },

  contribute: {
    requiredFieldsError: "Título, descripción y ciudad son obligatorios",
    licenseRequiredError: "Acepta la licencia CC BY 4.0 para publicar tu contribución",
    uploadingPhoto: "Subiendo foto",
    updatingStatus: "Actualizando…",
    savingStatus: "Guardando…",
    updateSuccess: "Contribución actualizada",
    submitSuccess: "Contribución enviada — pendiente de moderación",
    submitError: "Error al enviar la contribución",
    deleteConfirm: "¿Eliminar esta contribución?",
    deleteSuccess: "Eliminado",
    withdrawSuccess: "Solicitud de retiro enviada al equipo de moderación",
    headerTitle: "Contribuir",
    heroTitle: "Comparte tus joyas escondidas",
    heroSubtitle: "Ayuda a otros viajeros compartiendo las actividades y restaurantes que te encantaron. Cada contribución es revisada por nuestro equipo antes de publicarse.",
    tabNew: "Nueva",
    tabMine: "Mis contribuciones",
    editingNotice: "Editando una contribución pendiente",
    typeLabel: "Tipo",
    typeActivity: "Actividad",
    typeRestaurant: "Restaurante",
    mainInfoTitle: "Información principal",
    placeName: "Nombre del lugar",
    placeNamePlaceholderRestaurant: "Ej.: Casa Marcel",
    placeNamePlaceholderActivity: "Ej.: Cascada escondida del Monte Sagrado",
    descriptionLabel: "Descripción",
    descriptionPlaceholder: "¿Por qué es genial? ¿Qué se debe hacer o comer allí?",
    cityLabel: "Ciudad",
    cityPlaceholder: "Lisboa",
    countryLabel: "País",
    countryPlaceholder: "Portugal",
    addressLabel: "Dirección exacta",
    photosLabel: "Fotos",
    photoAltPrefix: "Foto de",
    photoAltGeneric: "Foto del lugar",
    deletePhotoLabel: "Eliminar foto",
    addPhotoButton: "Añadir",
    photosWord: "fotos",
    eachWord: "cada una",
    tipsCardTitle: "Consejos prácticos",
    tipsLabel: "Consejos y trucos",
    tipsPlaceholder: "Reserva con antelación, pide la especialidad del chef...",
    bestHoursLabel: "Mejores horarios",
    bestHoursPlaceholder: "9-11h o después de las 19h",
    crowdLevelLabel: "Nivel de afluencia",
    crowdLevelPlaceholder: "Nivel",
    crowdLow: "Tranquilo",
    crowdMedium: "Moderado",
    crowdHigh: "Muy concurrido",
    isPaidLabel: "¿De pago?",
    avgPriceLabel: "Precio medio / persona (€)",
    licenseCardTitle: "Licencia y atribución",
    authorNameLabel: "Nombre de autor mostrado (opcional)",
    authorNamePlaceholder: "Apodo, nombre o dejar en blanco",
    authorNameHint: "Si se deja en blanco, se usará tu apodo de perfil para la atribución pública.",
    licenseAgreementPrefix: "Publico bajo la licencia",
    licenseAgreementBody: "Conservo la propiedad de mi contenido (texto + fotos). Autorizo a Globr y a otros viajeros a reutilizarlo siempre que se cite mi atribución. Certifico que poseo los derechos de las fotos subidas y que puedo solicitar su retiro en cualquier momento.",
    preparingStatus: "Preparando…",
    updateButton: "Actualizar",
    submitButton: "Enviar para moderación",
    noContributions: "Aún no hay contribuciones.",
    statusPending: "Pendiente",
    statusApproved: "Aprobado",
    statusRejected: "Rechazado",
    statusWithdrawalRequested: "Retiro solicitado",
    statusWithdrawn: "Retirado",
    reasonLabel: "Motivo:",
    withdrawalReasonLabel: "Motivo del retiro:",
    editTitle: "Editar",
    requestWithdrawalTitle: "Solicitar retiro",
    withdrawButton: "Retirar",
    withdrawDialogDesc: "Tu contribución se ocultará inmediatamente al público y se enviará al equipo de moderación para su eliminación definitiva (incluidas las fotos).",
    withdrawReasonLabel: "Motivo (opcional)",
    withdrawReasonPlaceholder: "Lugar cerrado, fotos a retirar, etc.",
    confirmWithdrawButton: "Confirmar retiro",
  },

  esim: {
    title: "eSIM recomendadas para",
    subtitle: "Selección clasificada por precio, base de usuarios y calidad de red, con una bonificación por cobertura local.",
    cardsViewTitle: "Vista de tarjetas",
    cardsViewLabel: "Tarjetas",
    compareViewTitle: "Vista comparativa",
    compareViewLabel: "Comparar",
    hideFilters: "Ocultar filtros",
    showFilters: "Filtros y detalles",
    filtersLabel: "Filtros",
    resetButton: "Restablecer",
    maxPriceLabel: "Precio máx.",
    minNetworkLabel: "Calidad de red mín.",
    minReputationLabel: "Reputación mín. (M usuarios)",
    allLabel: "Todas",
    matchingProvidersLabel: "proveedor(es) coinciden con tus filtros.",
    defaultRankingNotice: "Mostrando la clasificación predeterminada.",
    criteriaHeader: "Criterio",
    bestChoiceBadge: "Mejor opción",
    internalScoreLabel: "Puntuación interna:",
    scoreTooltipSuffix: "(precio, red, base de usuarios, cobertura regional)",
    scoreLabel: "Puntuación",
    priceLabel: "Precio",
    networkLabel: "Red",
    userBaseLabel: "Base de usuarios",
    usersUnit: "usuarios",
    strongRegionsLabel: "Regiones fuertes",
    strengthLabel: "Fortaleza",
    linkHeader: "Enlace",
    viewOffersLink: "Ver ofertas",
    baseLabel: "Base",
    scoreDetailsLabel: "Detalles de la puntuación",
    exampleLabel: "Ej.:",
    pricingDisclaimer: "Precios indicativos · verifica el plan exacto en el sitio del proveedor antes de comprar.",
  },

  roadtrip: {
    totalDistance: "Distancia total",
    drivingTime: "Tiempo de conducción",
    breakTime: "Pausas",
    stopTime: "Paradas",
    optimizedOrder: "Orden optimizado",
    ofDriving: "de conducción",
    stopAt: "Parada en",
    arrival: "Llegada",
    departureDate: "Fecha de salida",
    departureTime: "Hora de salida",
    stopDuration: "Tiempo por parada",
    dieselPrice: "Diésel",
    livePrice: "precio en directo",
    stationsNearby: "estaciones cerca de la ruta",
    estimatedPrice: "precio estimado (fuera de Francia)",
    budgetTitle: "Presupuesto total del road trip",
    budgetFuel: "Combustible",
    budgetTolls: "Peajes",
    budgetStay: "Alojamiento",
    budgetMeals: "Comidas",
    budgetPerPerson: "Por persona",
    budgetTravelers: "viajeros",
    budgetTraveler: "viajero",
    budgetDays: "días",
    budgetNights: "noches",
    budgetNote: "Estimación: combustible y peajes según la ruta real; alojamiento y comidas estimados según tu nivel de presupuesto.",
    break: {
      break: "Pausa",
      meal: "Comida",
      fuel: "Repostaje",
    },
  },

  carRoute: {
    tollSectionsLabel: "tramo(s) de peaje",
    noTolls: "Sin peaje",
    trafficDelaySuffix: "min de tráfico",
    smoothTraffic: "Tráfico fluido",
    fuelStopsLabel: "parada(s) de combustible recomendada(s)",
  },

  blog: {
    seoTitle: "Blog de viajes: consejos, guías y trucos | Globr",
    seoDescription: "Consejos de viaje, guías por país y trucos prácticos: visado, presupuesto, seguridad, eSIM y transporte para preparar tu próximo viaje.",
    h1: "Blog de viajes: consejos y guías prácticas",
    intro: "Bienvenido al blog de Globr. Descubre nuestras guías prácticas por país — visado, presupuesto, seguridad, eSIM, transporte — y nuestros consejos para preparar tu viaje con tranquilidad.",
  },
} satisfies TranslationSchema;
